import { useEffect, useMemo, useState } from 'react';
import { RANKS, SUITS, handTotal, isPair } from '../game/engine.js';
import { basicStrategyAction, reasonFor } from '../game/basicStrategy.js';
import { Card, Hand } from '../components/Card.jsx';
import { CoachPanel } from '../components/CoachPanel.jsx';

// Generates a random realistic 2-card player hand + dealer upcard.
// Mix of categories so the drill stays varied.
const CATEGORIES = ['hard', 'soft', 'pair'];

function randomFrom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function randomCard() {
  return { rank: randomFrom(RANKS), suit: randomFrom(SUITS) };
}
function randomNonAceCard() {
  const r = randomFrom(RANKS.filter((x) => x !== 'A'));
  return { rank: r, suit: randomFrom(SUITS) };
}

function generateHand(category) {
  if (category === 'pair') {
    const r = randomFrom(RANKS);
    return [{ rank: r, suit: SUITS[0] }, { rank: r, suit: SUITS[1] }];
  }
  if (category === 'soft') {
    const ace = { rank: 'A', suit: randomFrom(SUITS) };
    // Pick a non-ace card so total is soft 13–20.
    const other = { rank: randomFrom(['2', '3', '4', '5', '6', '7', '8', '9']), suit: randomFrom(SUITS) };
    return [ace, other];
  }
  // hard: ensure no ace, no pair, total in 5–20.
  let cards;
  let attempts = 0;
  do {
    cards = [randomNonAceCard(), randomNonAceCard()];
    attempts++;
  } while ((isPair(cards) || handTotal(cards).total > 20) && attempts < 20);
  return cards;
}

function generateUpcard() {
  return { rank: randomFrom(RANKS), suit: randomFrom(SUITS) };
}

const ACTIONS = ['hit', 'stand', 'double', 'split', 'surrender'];

export function StrategyDrillMode({ stats, setStats, prefs }) {
  const [scenario, setScenario] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [filter, setFilter] = useState('mixed'); // mixed | hard | soft | pair

  function nextScenario() {
    const cat = filter === 'mixed' ? randomFrom(CATEGORIES) : filter;
    const player = generateHand(cat);
    const dealerUp = generateUpcard();
    const isHandPair = isPair(player);
    setScenario({ player, dealerUp, category: cat, isPair: isHandPair });
    setFeedback(null);
  }

  useEffect(() => { nextScenario(); /* eslint-disable-next-line */ }, [filter]);

  function pick(action) {
    if (!scenario || feedback) return;
    const optimal = basicStrategyAction(scenario.player, scenario.dealerUp.rank, {
      canDouble: true,
      canSurrender: true,
      canSplit: scenario.isPair,
      das: true,
    });
    const correct = action === optimal;
    setFeedback({
      correct,
      taken: action,
      suggested: optimal,
      reason: reasonFor(scenario.player, scenario.dealerUp.rank, optimal),
    });
    setStats((s) => ({
      total: (s.total || 0) + 1,
      correct: (s.correct || 0) + (correct ? 1 : 0),
      byCategory: {
        ...(s.byCategory || {}),
        [scenario.category]: {
          total: ((s.byCategory?.[scenario.category]?.total) || 0) + 1,
          correct: ((s.byCategory?.[scenario.category]?.correct) || 0) + (correct ? 1 : 0),
        },
      },
    }));
  }

  const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  return (
    <div className="px-4 py-6 max-w-2xl mx-auto flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl font-bold">Strategy Drill</h2>
        <div className="text-sm text-white/70">
          Accuracy: <b className="text-white">{accuracy}%</b> · {stats.total || 0} hands
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['mixed', 'hard', 'soft', 'pair'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest transition ${filter === f ? 'bg-chip text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>
            {f}
          </button>
        ))}
      </div>

      {scenario && (
        <div className="bg-black/20 rounded-lg p-5 border border-white/10 flex flex-col gap-5">
          <div className="flex flex-col items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-white/50">Dealer shows</div>
            <Card card={scenario.dealerUp} />
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-white/50">
              You have {handTotal(scenario.player).soft ? 'soft ' : ''}{handTotal(scenario.player).total}
              {scenario.isPair ? ` (pair)` : ''}
            </div>
            <Hand cards={scenario.player} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {ACTIONS.map((a) => (
          <button key={a} onClick={() => pick(a)}
            disabled={!!feedback || (a === 'split' && !scenario?.isPair)}
            className="px-3 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition disabled:opacity-30 disabled:cursor-not-allowed border border-white/20 capitalize">
            {a}
          </button>
        ))}
      </div>

      {feedback && (
        <>
          <CoachPanel correct={feedback.correct} taken={feedback.taken} suggested={feedback.suggested} reason={feedback.reason} />
          <button onClick={nextScenario}
            className="px-8 py-3 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition">
            Next hand
          </button>
        </>
      )}

      {stats.total > 0 && stats.byCategory && (
        <div className="bg-black/20 rounded-lg p-3 border border-white/10 grid grid-cols-3 gap-3 text-center text-xs">
          {['hard', 'soft', 'pair'].map((cat) => {
            const c = stats.byCategory[cat];
            const acc = c?.total ? Math.round((c.correct / c.total) * 100) : null;
            return (
              <div key={cat}>
                <div className="text-[10px] uppercase tracking-wider text-white/50">{cat}</div>
                <div className="text-white font-semibold">{acc !== null ? `${acc}%` : '—'}</div>
                <div className="text-white/40 text-[10px]">{c?.total || 0} hands</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
